import { useEffect, useState } from 'react'
import { zonedTimeToUtc } from 'date-fns-tz'

import { addCalendarDays, calendarDateInTimeZone } from '@scrambl/shared'

/** A reporting-day clock: no requests, and refreshes after browser/native resume. */
export function useCalendarDay(timezone: string): string {
  const [clock, setClock] = useState(() => ({
    timezone,
    day: calendarDateInTimeZone(new Date(), timezone),
  }))

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const refresh = () => {
      clearTimeout(timer)
      const now = new Date()
      const currentDay = calendarDateInTimeZone(now, timezone)
      setClock(previous =>
        previous.timezone === timezone && previous.day === currentDay
          ? previous
          : { timezone, day: currentDay },
      )
      const nextMidnight = zonedTimeToUtc(
        `${addCalendarDays(currentDay, 1)}T00:00:00`,
        timezone,
      )
      // Rechecking locally also handles clock corrections. Most ticks don't
      // change state, so consumers don't refetch or rerender every minute.
      // Some zones skip midnight at a DST transition. If the conversion
      // resolves into the preceding day, keep the minute cadence instead of
      // repeatedly scheduling an already-past midnight.
      const untilMidnight = nextMidnight.getTime() - now.getTime() + 10
      timer = setTimeout(
        refresh,
        untilMidnight > 0 ? Math.min(60_000, untilMidnight) : 60_000,
      )
    }
    refresh()
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [timezone])

  // Avoid returning the preceding timezone's day for one render after a
  // preference change; the effect then updates the retained clock state.
  return clock.timezone === timezone
    ? clock.day
    : calendarDateInTimeZone(new Date(), timezone)
}
